import { FC } from 'react'

import { styled } from 'styled-components'
import { v4 as uuid4 } from 'uuid'

import { LoadingContainer } from 'peach/components'
import { StyledButton as Button } from 'peach/components/Button/Button'
import Input from 'peach/components/Input'

import { R } from 'core/helpers'
import { useForm } from 'core/hooks'
import {
  emailValidationRegex,
  useInternalEmployeeAddToTeam,
  useInternalEmployeeCreate,
  useInternalUserCreate,
} from 'core/types'

import { ErrorComponent, FormActions, Title } from './Components'
import { Company } from './types'

const profiles = [
  {
    email: 'eddie@peachfinance.com',
    firstName: 'Eddie',
    lastName: 'Oistacher',
  },
  {
    email: 'bradley.king@peachfinance.com',
    firstName: 'Brad',
    lastName: 'King',
  },
  {
    email: 'russell.braden@peachfinance.com',
    firstName: 'Russell',
    lastName: 'Braden',
  },
  {
    email: 'chris.kruse@peachfinance.com',
    firstName: 'Chris',
    lastName: 'Kruse',
  },
  {
    email: 'stephen.dolan@peachfinance.com',
    firstName: 'Stephen',
    lastName: 'Dolan',
  },
  {
    email: 'amanda.cotter@peachfinance.com',
    firstName: 'Amanda',
    lastName: 'Cotter',
  },
  {
    email: 'elena.sawchuk@peachfinance.com',
    firstName: 'Elena',
    lastName: 'Sawchuk',
  },
  {
    email: 'julie.fisher@peachfinance.com',
    firstName: 'Julie',
    lastName: 'Fisher',
  },
  {
    email: 'mike.hammer@peachfinance.com',
    firstName: 'Mike',
    lastName: 'Hammer',
  },
  // {
  //   email: 'nicolas.bello@peachfinance.com',
  //   firstName: 'Nicolas',
  //   lastName: 'Bello',
  // },
  // {
  //   email: 'veronica.villalba@peachfinance.com',
  //   firstName: 'Veronica',
  //   lastName: 'Villalba',
  // },
  {
    email: 'michael.rosenthal@peachfinance.com',
    firstName: 'Michael',
    lastName: 'Rosenthal',
  },
    {
    email: 'cfajardo@galileo-ft.com',
    firstName: 'Cindy',
    lastName: 'Fajardo',
  },
  {
    email: 'cpettit@galileo-ft.com',
    firstName: 'Cody',
    lastName: 'Pettit',
  },
  {
    email: 'ddeniker@galileo-ft.com',
    firstName: 'DeAnn',
    lastName: 'Deniker',
  },
  {
    email: 'dstephenson@galileo-ft.com',
    firstName: 'Dylan',
    lastName: 'Stephenson',
  },
  {
    email: 'dyeager@galileo-ft.com',
    firstName: 'David',
    lastName: 'Yeager',
  },
  {
    email: 'jdunkley@galileo-ft.com',
    firstName: 'Jeffrey',
    lastName: 'Dunkley',
  },
  {
    email: 'jmagure@galileo-ft.com',
    firstName: 'Jacob',
    lastName: 'Magure',
  },
  {
    email: 'keruiz@galileo-ft.com',
    firstName: 'Kearsten',
    lastName: 'Ruiz',
  },
  {
    email: 'kgordy@galileo-ft.com',
    firstName: 'Kaitlyn',
    lastName: 'Gordy',
  },
  {
    email: 'lhermosillo@galileo-ft.com',
    firstName: 'Lyza',
    lastName: 'Hermosillo',
  },
  {
    email: 'lmartin@galileo-ft.com',
    firstName: 'Leah',
    lastName: 'Martin',
  },
  {
    email: 'mcallen@galileo-ft.com',
    firstName: 'McKensie',
    lastName: 'Allen',
  },
  {
    email: 'migonzalez@galileo-ft.com',
    firstName: 'Minerva',
    lastName: 'Gonzalez',
  },
  {
    email: 'rsteele@galileo-ft.com',
    firstName: 'Remington',
    lastName: 'Steele',
  },
  {
    email: 'rvanderhoef@galileo-ft.com',
    firstName: 'Robert',
    lastName: 'Vanderhoef',
  },
  {
    email: 'scoronado@galileo-ft.com',
    firstName: 'Sherly',
    lastName: 'Coronado',
  },
  {
    email: 'pwinn@galileo-ft.com',
    firstName: 'Philip',
    lastName: 'Winn',
  },
]

type User = {
  rowId?: string
  type?: 'agent' | 'borrower' | 'service'
  roleIds?: Array<string>
  authType: {
    email?: string
  }
  firstName?: string
  lastName?: string
}

type CreateUsersFormValues = {
  users: Array<User>
}

type CreateUsersFormProps = {
  company: Company
  updateCompany: (obj: Company) => void
  title: string
}

const CreateUsersForm: FC<CreateUsersFormProps> = ({
  company,
  updateCompany,
  title,
}) => {
  const companyId = company?.companyId

  const { mutateAsync: addCompanyUser } = useInternalUserCreate({
    companyId,
  })

  const { mutateAsync: addCompanyEmployee } = useInternalEmployeeCreate({
    companyId,
  })

  const { mutateAsync: addEmployeeToTeam } = useInternalEmployeeAddToTeam({
    companyId,
    employeeId: undefined,
    teamId: undefined,
  })

  const { fields, formState, handleSubmit } = useForm<CreateUsersFormValues>(
    {
      users: R.map(profiles, (profile) => ({
        rowId: uuid4(),
        type: 'agent',
        roleIds: R.pipe(
          company?.roles ?? [],
          R.reject(
            (role) => role.alias === 'api' || role.alias === 'peach-support',
          ),
          R.map((role) => role.id),
        ) as Array<string>,
        authType: { email: profile.email },
        firstName: profile.firstName,
        lastName: profile.lastName,
      })),
    },
    {
      onSubmit: async (values) => {
        if (!companyId) {
          return
        }

        const teamForAssignment =
          R.find(
            company?.teams ?? [],
            (team) => team.name === 'Peach Tier 2 Support',
          ) ?? company?.teams?.[0]

        const teamId = teamForAssignment?.id

        const newUsers: Array<{
          type?: string
          userId?: string
          id?: string
        }> = []

        const newEmployees: Array<{
          id?: string
          firstName?: string
          lastName?: string
          teams: Array<{ name: string }>
        }> = []

        for (const user of values.users) {
          const userResp = await addCompanyUser({
            type: user.type,
            roleIds: user.roleIds,
            authType: user.authType,
          })

          const userData = userResp?.data
          if (!userData?.id) {
            continue
          }

          const employeeResp = await addCompanyEmployee({
            firstName: user.firstName,
            lastName: user.lastName,
            userId: userData.id,
            email: user.authType?.email,
          })

          const employeeData = employeeResp?.data
          if (employeeData?.id && teamId) {
            await addEmployeeToTeam(undefined, {
              employeeId: employeeData.id,
              teamId,
            })
          }

          newUsers.push({
            type: userData.type,
            userId:
              userData.userId !== undefined && userData.userId !== null
                ? String(userData.userId)
                : undefined,
            id: userData.id,
          })

          if (employeeData?.id) {
            newEmployees.push({
              id: employeeData.id,
              firstName: employeeData.firstName,
              lastName: employeeData.lastName,
              teams: [{ name: teamForAssignment?.name ?? '' }],
            })
          }
        }

        updateCompany({
          ...company,
          users: [...(company?.users ?? []), ...newUsers],
          employees: [...(company?.employees ?? []), ...newEmployees],
        })
      },
      // Validate: at least one user; each user has non-empty >=3 char names and valid email
      rules: {
        users: (arr) => {
          if (!R.isArray(arr) || R.isEmpty(arr)) {
            return 'At least one user is required'
          }

          for (const user of arr) {
            const email = user?.authType?.email ?? ''
            const first = user?.firstName ?? ''
            const last = user?.lastName ?? ''

            if (!emailValidationRegex.test(email)) return 'Email is not valid'
            if (
              first.trim().length < 3 ||
              last.trim().length < 3 ||
              email.trim().length < 3
            )
              return 'Fields must be at least 3 characters'
          }

          return true
        },
      },
      validationType: 'onBlur',
    },
  )

  const users = fields.users.value

  return (
    <LoadingContainer loading={formState.isSubmitting}>
      <Title>{title}</Title>
      <form onSubmit={handleSubmit}>
        {R.map.indexed(users, (user, index) => (
          <Row key={user.rowId ?? String(index)}>
            <Input
              label='email'
              onBlur={fields.users.onBlur}
              onChange={(e) => {
                const next = [...users]
                next[index] = {
                  ...user,
                  authType: { ...(user.authType ?? {}), email: e.target.value },
                }
                fields?.users?.onChange?.(next)
              }}
              required
              type='text'
              value={user.authType?.email ?? ''}
            />
            <Input
              label='first name'
              onBlur={fields.users.onBlur}
              onChange={(e) => {
                const next = [...users]
                next[index] = { ...user, firstName: e.target.value }
                fields?.users?.onChange?.(next)
              }}
              required
              type='text'
              value={user.firstName ?? ''}
            />
            <Input
              label='last name'
              onBlur={fields.users.onBlur}
              onChange={(e) => {
                const next = [...users]
                next[index] = { ...user, lastName: e.target.value }
                fields?.users?.onChange?.(next)
              }}
              required
              type='text'
              value={user.lastName ?? ''}
            />
            <RemoveButton
              onClick={() =>
                fields?.users?.onChange?.(
                  R.reject.indexed(users, (_, i) => i === index),
                )
              }
            >
              remove
            </RemoveButton>
          </Row>
        ))}

        <AddButton
          onClick={() =>
            fields?.users?.onChange?.([
              ...users,
              {
                rowId: uuid4(),
                type: 'agent',
                roleIds: R.pipe(
                  company?.roles ?? [],
                  R.reject(
                    (role) =>
                      role.alias === 'api' || role.alias === 'peach-support',
                  ),
                  R.map((role) => role.id),
                ) as Array<string>,
                authType: { email: '' },
              },
            ])
          }
        >
          add
        </AddButton>

        {/* show array-level validation error if any */}
        {fields.users.errorMessage && (
          <ErrorComponent>{fields.users.errorMessage}</ErrorComponent>
        )}

        <FormActions>
          <Button $primary disabled={!formState.isValid} type='submit'>
            Submit
          </Button>
        </FormActions>
      </form>
    </LoadingContainer>
  )
}

export default CreateUsersForm

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: start;
`

const RemoveButton = styled(Button)`
  margin-top: 18px;
`

const AddButton = styled(Button)`
  margin-top: 8px;
`
